import { useState } from "react";
import { toast } from "sonner";
import { Send, Loader2, Mail, Phone, MapPin } from "lucide-react";
import { submitContact } from "../lib/api";

export default function ContactSection() {
    const [form, setForm] = useState({
        name: "",
        company: "",
        email: "",
        phone: "",
        message: "",
        website: "", // honeypot
    });
    const [acceptPrivacy, setAcceptPrivacy] = useState(false);
    const [loading, setLoading] = useState(false);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Udfyld navn, email og besked.");
            return;
        }
        if (!acceptPrivacy) {
            toast.error("Du skal acceptere privatlivspolitikken for at sende beskeden.");
            return;
        }
        setLoading(true);
        try {
            await submitContact(form);
            toast.success("Tak! Vi vender tilbage hurtigst muligt.");
            setForm({ name: "", company: "", email: "", phone: "", message: "", website: "" });
            setAcceptPrivacy(false);
        } catch (err) {
            if (err?.response?.status === 429) {
                toast.error("For mange forsøg — prøv igen om et minut.");
            } else {
                toast.error("Kunne ikke sende — prøv igen senere.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="kontakt" className="py-24 md:py-32 bg-white" data-testid="contact-section">
            <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
                <div className="lg:col-span-5">
                    <div className="inline-flex items-center gap-2 rounded-full bg-sky-50 border border-sky-100 px-3 py-1 text-xs font-semibold text-sky-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-sky-500" /> Kontakt
                    </div>
                    <h2 className="mt-5 font-display text-3xl md:text-5xl font-bold tracking-tight text-slate-900">
                        Bliv beta-tester eller spørg os.
                    </h2>
                    <p className="mt-4 text-base text-slate-600 leading-relaxed">
                        Vil du forme fremtidens driftsplatform sammen med os? Skriv til os —
                        vi tager altid en uforpligtende snak.
                    </p>

                    <div className="mt-8 space-y-4">
                        <ContactItem icon={Mail} label="Email" value="info@cleany.dk" href="mailto:info@cleany.dk" />
                        <ContactItem icon={Phone} label="Telefon" value="+45 70 22 19 90" href="tel:+4570221990" />
                        <ContactItem icon={MapPin} label="Adresse" value="Strevelinsvej 16, 7000 Fredericia" />
                    </div>
                </div>

                <div className="lg:col-span-7">
                    <form
                        onSubmit={onSubmit}
                        className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm"
                        data-testid="contact-form"
                    >
                        {/* Honeypot — hidden from users, bots fill it */}
                        <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px", height: 0, width: 0, overflow: "hidden" }}>
                            <label htmlFor="contact-hp-website">Website (lad stå tom)</label>
                            <input
                                id="contact-hp-website"
                                type="text"
                                name="website"
                                value={form.website}
                                onChange={update("website")}
                                tabIndex={-1}
                                autoComplete="off"
                            />
                        </div>
                        <div className="grid md:grid-cols-2 gap-4">
                            <Field
                                label="Navn"
                                placeholder="Fornavn Efternavn"
                                value={form.name}
                                onChange={update("name")}
                                testid="contact-input-name"
                                required
                            />
                            <Field
                                label="Firma"
                                placeholder="Dit firma"
                                value={form.company}
                                onChange={update("company")}
                                testid="contact-input-company"
                            />
                            <Field
                                label="Email"
                                type="email"
                                placeholder="navn@firma.dk"
                                value={form.email}
                                onChange={update("email")}
                                testid="contact-input-email"
                                required
                            />
                            <Field
                                label="Telefon"
                                placeholder="+45 ..."
                                value={form.phone}
                                onChange={update("phone")}
                                testid="contact-input-phone"
                            />
                        </div>
                        <div className="mt-4">
                            <label className="text-xs font-semibold text-slate-700">Besked</label>
                            <textarea
                                value={form.message}
                                onChange={update("message")}
                                rows={5}
                                required
                                placeholder="Hvad har I brug for? Hvor stor er jeres virksomhed?"
                                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-200 resize-none"
                                data-testid="contact-input-message"
                            />
                        </div>
                        <label className="mt-4 flex items-start gap-2.5 cursor-pointer select-none" data-testid="contact-privacy-row">
                            <input
                                type="checkbox"
                                checked={acceptPrivacy}
                                onChange={(e) => setAcceptPrivacy(e.target.checked)}
                                className="mt-0.5 h-4 w-4 accent-sky-600 cursor-pointer shrink-0"
                                data-testid="contact-input-privacy"
                            />
                            <span className="text-[12px] text-slate-600 leading-snug">
                                Jeg accepterer at Cleany behandler mine oplysninger ifm.
                                denne henvendelse i henhold til{" "}
                                <a
                                    href="/privatlivspolitik"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sky-700 hover:text-sky-900 underline underline-offset-2 font-medium"
                                >
                                    privatlivspolitikken
                                </a>
                                .
                            </span>
                        </label>
                        <button
                            type="submit"
                            disabled={loading}
                            className="mt-5 inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white font-medium h-11 px-6 transition-all active:scale-[0.98]"
                            data-testid="contact-submit"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" /> Sender...
                                </>
                            ) : (
                                <>
                                    <Send className="h-4 w-4" /> Send besked
                                </>
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </section>
    );
}

function Field({ label, type = "text", testid, ...props }) {
    return (
        <div>
            <label className="text-xs font-semibold text-slate-700">{label}</label>
            <input
                type={type}
                data-testid={testid}
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-200"
                {...props}
            />
        </div>
    );
}

function ContactItem({ icon: Icon, label, value, href }) {
    const Inner = (
        <div className="flex items-start gap-3 group">
            <div className="h-10 w-10 rounded-xl bg-sky-50 border border-sky-100 flex items-center justify-center text-sky-600 shrink-0">
                <Icon className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <div>
                <div className="text-xs uppercase tracking-wider font-semibold text-slate-400">{label}</div>
                <div className="text-base font-medium text-slate-900 group-hover:text-sky-700 transition-colors">{value}</div>
            </div>
        </div>
    );
    return href ? <a href={href}>{Inner}</a> : <div>{Inner}</div>;
}
